import { useEffect, useState } from 'react';
import axios from 'axios';

// Last-resort fallback only — used when there is no team context yet or a fetch
// fails. The live options come from the team's Age Restriction settings (venue-
// scoped when a venue ownership is known, team-wide otherwise), so deletions and
// custom values configured on the settings card are always respected.
const LEGACY_OPTIONS = [
  { value: 'All Ages', label: 'All Ages', default: false },
  { value: 'Under 18 must be accompanied by parent or guardian', label: 'Under 18 must be accompanied by parent or guardian', default: false },
  { value: '13+', label: '13+', default: false },
  { value: '16+', label: '16+', default: false },
  { value: '17+', label: '17+', default: false },
  { value: '18+', label: '18+', default: false },
  { value: '19+', label: '19+', default: false },
  { value: '21+', label: '21+', default: false },
];

// Module-scoped cache: lives for the page lifetime. Admin edits in another tab won't be reflected until refresh.
const cache = new Map();

const fetchVenueOptions = (teamId, venueOwnershipId) =>
  axios.get(`/teams/${teamId}/venue_ownerships/${venueOwnershipId}/age_restrictions/options`)
    .then((res) => (Array.isArray(res.data) ? res.data : LEGACY_OPTIONS));

const fetchTeamOptions = (teamId) =>
  axios.get(`/teams/${teamId}/age_restriction_settings`)
    .then((res) => (Array.isArray(res.data)
      ? res.data.map((setting) => ({ value: setting.name, label: setting.name, default: false }))
      : LEGACY_OPTIONS));

const useVenueAgeRestrictionOptions = (teamId, venueOwnershipId) => {
  const cacheKey = teamId
    ? (venueOwnershipId ? `vo:${teamId}:${venueOwnershipId}` : `team:${teamId}`)
    : null;
  const cached = cacheKey ? cache.get(cacheKey) : null;

  const [options, setOptions] = useState(cached || LEGACY_OPTIONS);
  const [loading, setLoading] = useState(Boolean(cacheKey && !cached));

  useEffect(() => {
    if (!cacheKey) {
      setOptions(LEGACY_OPTIONS);
      setLoading(false);
      return;
    }

    const hit = cache.get(cacheKey);
    if (hit) {
      setOptions(hit);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const fetchOptions = venueOwnershipId
      ? fetchVenueOptions(teamId, venueOwnershipId)
      : fetchTeamOptions(teamId);
    fetchOptions
      .then((normalized) => {
        if (cancelled) return;
        cache.set(cacheKey, normalized);
        setOptions(normalized);
      })
      .catch(() => {
        if (cancelled) return;
        setOptions(LEGACY_OPTIONS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [cacheKey, teamId, venueOwnershipId]);

  const defaultOption = options.find((o) => o.default);
  const defaultValue = defaultOption ? defaultOption.value : null;

  return { options, defaultValue, loading };
};

export default useVenueAgeRestrictionOptions;
